import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Trash2, Loader2, Clock, Image, FileVideo, Plus, Upload, Link2, Pencil } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const MemesAdmin = () => {
  const { token } = useAuth();
  const [memes, setMemes] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [addOpen, setAddOpen] = useState(false);
  const [addMode, setAddMode] = useState("url");
  const [addTitle, setAddTitle] = useState("");
  const [addUrl, setAddUrl] = useState("");
  const [addFile, setAddFile] = useState(null);
  const [addMediaType, setAddMediaType] = useState("image");
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    try {
      const [allRes, pendingRes] = await Promise.all([
        axios.get(`${API}/memes/all`, { headers }),
        axios.get(`${API}/memes/pending`, { headers }),
      ]);
      setMemes(allRes.data);
      setPending(pendingRes.data);
    } catch (e) {
      toast.error("Failed to load memes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const approve = async (id) => {
    try {
      await axios.post(`${API}/memes/${id}/approve`, {}, { headers });
      toast.success("Meme approved");
      fetchAll();
    } catch { toast.error("Failed to approve"); }
  };

  const reject = async (id) => {
    try {
      await axios.post(`${API}/memes/${id}/reject`, {}, { headers });
      toast.success("Meme rejected");
      fetchAll();
    } catch { toast.error("Failed to reject"); }
  };

  const deleteMeme = async (id) => {
    if (!window.confirm("Delete this meme permanently?")) return;
    try {
      await axios.delete(`${API}/memes/${id}`, { headers });
      toast.success("Meme deleted");
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  const saveTitle = async (id) => {
    try {
      await axios.put(`${API}/memes/${id}`, { title: editTitle }, { headers });
      toast.success("Title updated");
      setEditingId(null);
      fetchAll();
    } catch { toast.error("Failed to update title"); }
  };

  const triggerScraper = async () => {
    try {
      await axios.post(`${API}/memes/scrape`, {}, { headers });
      toast.success("Scraper started in background (@allwhufc on IG & TikTok)");
    } catch { toast.error("Failed to start scraper"); }
  };

  const handleAdd = async () => {
    setSubmitting(true);
    try {
      let imageUrl = addUrl.trim();
      let mType = addMediaType;

      if (addMode === "file" && addFile) {
        const formData = new FormData();
        formData.append("file", addFile);
        const uploadRes = await axios.post(`${API}/upload`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
        imageUrl = `/api/files/${uploadRes.data.storage_path}`;
        if (addFile.type.startsWith("video/")) mType = "video";
      }

      if (!imageUrl) {
        toast.error("Please provide an image/video");
        setSubmitting(false);
        return;
      }

      if (/\.(mp4|webm|mov)(\?|$)/i.test(imageUrl)) mType = "video";

      await axios.post(`${API}/memes`, {
        title: addTitle,
        image_url: imageUrl,
        media_type: mType,
        source: "admin",
      }, { headers });

      toast.success("Meme added (auto-approved)");
      setAddOpen(false);
      setAddTitle("");
      setAddUrl("");
      setAddFile(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add meme");
    } finally {
      setSubmitting(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const colors = {
      approved: "bg-green-100 text-green-700",
      pending: "bg-amber-100 text-amber-700",
      rejected: "bg-red-100 text-red-700",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || "bg-slate-100 text-slate-600"}`}>
        {status}
      </span>
    );
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#7C2C3B]" /></div>;

  return (
    <div className="space-y-4" data-testid="memes-admin">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold" style={{ fontFamily: "Barlow Condensed" }}>
          MEMES {pending.length > 0 && <span className="ml-2 bg-amber-500 text-white text-xs px-2 py-1 rounded-full" data-testid="pending-count">{pending.length} pending</span>}
        </h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={triggerScraper} data-testid="scrape-btn">
            Scrape @allwhufc
          </Button>
          <Button size="sm" onClick={() => setAddOpen(true)} className="bg-[#7C2C3B] hover:bg-[#6B2533]" data-testid="add-meme-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Meme
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        {["pending", "all"].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? "border-[#7C2C3B] text-[#7C2C3B]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`tab-${t}`}
          >
            {t === "pending" ? `Pending (${pending.length})` : `All (${memes.length})`}
          </button>
        ))}
      </div>

      {/* Pending Tab */}
      {tab === "pending" && (
        pending.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No pending submissions</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pending.map(meme => (
              <div key={meme.id} className="bg-white border border-amber-200 rounded-xl overflow-hidden" data-testid={`pending-meme-${meme.id}`}>
                <div className="aspect-video relative overflow-hidden bg-slate-100">
                  {meme.media_type === "video" ? (
                    <video src={meme.image_url} className="w-full h-full object-cover" controls muted />
                  ) : (
                    <img src={meme.image_url} alt={meme.title} className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
                  )}
                  <div className="absolute top-2 right-2">
                    {meme.media_type === "video" ? <FileVideo className="w-5 h-5 text-white drop-shadow" /> : <Image className="w-5 h-5 text-white drop-shadow" />}
                  </div>
                </div>
                <div className="p-3">
                  {editingId === meme.id ? (
                    <div className="flex items-center gap-1 mb-1">
                      <Input
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        className="h-7 text-sm"
                        autoFocus
                        onKeyDown={e => { if (e.key === "Enter") saveTitle(meme.id); if (e.key === "Escape") setEditingId(null); }}
                      />
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600 shrink-0" onClick={() => saveTitle(meme.id)}>
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 group/ptitle">
                      <p className="text-sm font-medium text-slate-800 truncate">{meme.title || "Untitled"}</p>
                      <button className="opacity-0 group-hover/ptitle:opacity-100 shrink-0" onClick={() => { setEditingId(meme.id); setEditTitle(meme.title || ""); }}>
                        <Pencil className="w-3 h-3 text-slate-400 hover:text-[#7C2C3B]" />
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-slate-400 mt-0.5">By {meme.submitted_by} · {new Date(meme.date_added).toLocaleDateString("en-GB")}</p>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => approve(meme.id)} data-testid={`approve-${meme.id}`}>
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="destructive" className="flex-1" onClick={() => reject(meme.id)} data-testid={`reject-${meme.id}`}>
                      <X className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* All Tab */}
      {tab === "all" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Preview</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Title</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Source</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Type</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Submitted By</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {memes.map(meme => (
                <tr key={meme.id} className="hover:bg-slate-50" data-testid={`meme-row-${meme.id}`}>
                  <td className="px-4 py-2">
                    <div className="w-12 h-12 rounded overflow-hidden bg-slate-100">
                      {meme.media_type === "video" ? (
                        <video src={meme.image_url} className="w-full h-full object-cover" muted />
                      ) : (
                        <img src={meme.image_url} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 max-w-[200px]">
                    {editingId === meme.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={editTitle}
                          onChange={e => setEditTitle(e.target.value)}
                          className="h-7 text-sm"
                          autoFocus
                          onKeyDown={e => { if (e.key === "Enter") saveTitle(meme.id); if (e.key === "Escape") setEditingId(null); }}
                          data-testid={`edit-title-input-${meme.id}`}
                        />
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600 shrink-0" onClick={() => saveTitle(meme.id)} data-testid={`save-title-${meme.id}`}>
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 shrink-0" onClick={() => setEditingId(null)}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 group/title">
                        <span className="truncate">{meme.title || "—"}</span>
                        <button
                          className="opacity-0 group-hover/title:opacity-100 transition-opacity shrink-0"
                          onClick={() => { setEditingId(meme.id); setEditTitle(meme.title || ""); }}
                          data-testid={`edit-title-btn-${meme.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5 text-slate-400 hover:text-[#7C2C3B]" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 capitalize">{meme.source}</td>
                  <td className="px-4 py-2 capitalize">{meme.media_type || "image"}</td>
                  <td className="px-4 py-2"><StatusBadge status={meme.status} /></td>
                  <td className="px-4 py-2 text-slate-500">{meme.submitted_by || "—"}</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      {meme.status === "pending" && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-green-600" onClick={() => approve(meme.id)}>
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => deleteMeme(meme.id)} data-testid={`delete-${meme.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Meme Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Barlow Condensed" }}>ADD MEME</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex gap-2">
              <button onClick={() => setAddMode("url")} className={`flex-1 py-2 rounded-lg text-sm flex items-center justify-center gap-1 ${addMode === "url" ? "bg-[#7C2C3B] text-white" : "bg-slate-100"}`}>
                <Link2 className="w-4 h-4" /> URL
              </button>
              <button onClick={() => setAddMode("file")} className={`flex-1 py-2 rounded-lg text-sm flex items-center justify-center gap-1 ${addMode === "file" ? "bg-[#7C2C3B] text-white" : "bg-slate-100"}`}>
                <Upload className="w-4 h-4" /> Upload
              </button>
            </div>
            <Input placeholder="Title (optional)" value={addTitle} onChange={e => setAddTitle(e.target.value)} data-testid="admin-meme-title" />
            {addMode === "url" ? (
              <Input placeholder="Image or video URL" value={addUrl} onChange={e => setAddUrl(e.target.value)} data-testid="admin-meme-url" />
            ) : (
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm"
                onChange={e => {
                  const f = e.target.files[0];
                  setAddFile(f);
                  if (f?.type.startsWith("video/")) setAddMediaType("video");
                  else setAddMediaType("image");
                }}
                className="w-full text-sm"
                data-testid="admin-meme-file"
              />
            )}
            <Button className="w-full bg-[#7C2C3B] hover:bg-[#6B2533]" onClick={handleAdd} disabled={submitting} data-testid="admin-meme-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Meme (Auto-Approved)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemesAdmin;
